import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { Table, Thead, Th, Tr, Td, SectionRow } from '../../../../ui/canon/DataTable';
import InfoTooltip from '../common/InfoTooltip';
import { useCurrency } from '../../hooks/useCurrency';

const resolveVar = (name) => {
  try {
    if (typeof window === 'undefined' || !window.document) return `var(${name})`;
    const v = getComputedStyle(document.documentElement).getPropertyValue(name);
    return (v && v.trim()) ? v.trim() : `var(${name})`;
  } catch (e) {
    return `var(${name})`;
  }
};

export default function DCFProjectionsTable({ data = [], className = '', showDiscountFactor = false, wealthPayoutPercent = null, showDirectExpenses = true, directExpensesOverride = null, enabledLines = { wealth: true, life: true, pc: true, group: true } }) {
  const intl = useIntl();
  const { code: _curCode, locale: _curLocale } = useCurrency();
  const currency0 = useMemo(() => new Intl.NumberFormat(_curLocale, { style: 'currency', currency: _curCode, maximumFractionDigits: 0, minimumFractionDigits: 0 }), [_curCode, _curLocale]);
  const formatCurrency = (value) => {
    const v = Number(value || 0);
    return currency0.format(v);
  };

  const formatDecimal = (value) => {
    const v = Number(value || 0);
    return v.toFixed(3);
  };

  const formatPercent = (value) => {
    const v = Number(value || 0);
    return `${(v * 100).toFixed(1)}%`;
  };
  const normalizePct = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return n > 1 ? n / 100 : n;
  };

  const payout = normalizePct(wealthPayoutPercent);

  const inferredTaxRate = useMemo(() => {
    for (let idx = 0; idx < data.length; idx += 1) {
      const proj = data[idx] || {};
      const net = Number.isFinite(Number(proj.netRevenue))
        ? Number(proj.netRevenue)
        : (Number(proj.wealthRevenue || 0) * payout + Number(proj.lifeRevenue || 0) + Number(proj.pcRevenue || 0) + Number(proj.groupRevenue || 0));
      const direct = showDirectExpenses ? Number(proj.directExpenses || 0) : 0;
      const operating = Number(proj.operatingExpenses || 0);
      const ebit = net - direct - operating;
      const tax = Number(proj.tax || 0);
      if (ebit > 0 && tax > 0) {
        const r = tax / ebit;
        if (Number.isFinite(r) && r >= 0 && r <= 1) return r;
      }
    }
    return 0;
  }, [data, payout, showDirectExpenses]); // Note: payout here is used only if netRevenue missing in original data

  // Re-calculate all derived values based on the live payout prop (optimistic update)
  const liveData = useMemo(() => {
    // Owner's Compensation Percentage logic (matches backend and MultiLineValuation.jsx)
    const getOwnersCompRate = (bnr) => {
      if (bnr < 250000.0) return 0.401;
      if (bnr < 500000.0) return 0.44;
      if (bnr < 1000000.0) return 0.264;
      if (bnr < 2000000.0) return 0.202;
      if (bnr < 3000000.0) return 0.194;
      if (bnr < 5000000.0) return 0.189;
      if (bnr < 7000000.0) return 0.132;
      if (bnr < 10000000.0) return 0.193;
      if (bnr < 15000000.0) return 0.183;
      return 0.141;
    };

    return data.map((proj) => {
      // 1. Calculate Net Revenue using the live payout
      const wealth = Number(proj.wealthRevenue || 0);
      const life = Number(proj.lifeRevenue || 0);
      const pc = Number(proj.pcRevenue || 0);
      const group = Number(proj.groupRevenue || 0);
      
      const effectivePayout = wealthPayoutPercent !== null && wealthPayoutPercent !== undefined ? payout : 0;
      const hasLivePayout = wealthPayoutPercent !== null && wealthPayoutPercent !== undefined;

      const net = hasLivePayout
        ? (wealth * effectivePayout + life + pc + group)
        : (Number.isFinite(Number(proj.netRevenue)) ? Number(proj.netRevenue) : (wealth * effectivePayout + life + pc + group));

      // 2. Calculate Direct Expenses (Owner's Comp) dynamically if enabled
      let direct = 0;
      if (showDirectExpenses) {
        if (directExpensesOverride != null) {
          // User has set a directExpenses override — use backend-computed per-year value
          // (proj.directExpenses already contains the override grown at expense growth rate)
          direct = Number(proj.directExpenses || 0);
        } else if (hasLivePayout) {
          // No override: optimistic recalculation for live payout changes
          const branchNetRev = wealth * effectivePayout;
          const rate = getOwnersCompRate(branchNetRev);
          direct = branchNetRev * rate;
        } else {
          // Fallback to existing data
          direct = Number(proj.directExpenses || 0);
        }
      }

      // 3. Calculate EBIT
      const operating = Number(proj.operatingExpenses || 0);
      const ebit = net - direct - operating;

      // 4. Calculate Tax using inferred rate
      const tax = (ebit > 0) ? ebit * inferredTaxRate : 0;

      // 5. Calculate FCF (Simplified: EBIT - Tax)
      const fcf = ebit - tax;

      // 6. Calculate PV
      const df = Number(proj.discountFactor || 0);
      const pv = fcf * df;

      return {
        ...proj,
        netRevenue: net,
        directExpenses: direct,
        ebit,
        tax,
        fcf,
        presentValue: pv
      };
    });
  }, [data, payout, wealthPayoutPercent, showDirectExpenses, inferredTaxRate, directExpensesOverride]);

  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({ opacity: 1, x: 0, transition: { delay: i * 0.05, duration: 0.3 } }),
  };

  const revenueBg = resolveVar('--succession-secondary-10');
  const expenseBg = resolveVar('--succession-error-bg');
  const fcfBg = resolveVar('--succession-success-bg');
  const pvBg = resolveVar('--succession-accent-10');

  return (
    <Table className={className}>
      <Thead>
        <Th sticky />
        {liveData.map((proj, idx) => (
          <Th key={idx}>{proj.year}</Th>
        ))}
      </Thead>
      <tbody>
        <motion.tr custom={0} initial="hidden" animate="visible" variants={rowVariants} style={{ background: revenueBg }}>
          <Td sticky align="left" className="font-semibold" style={{ background: revenueBg }}>{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_revenue_projections' })}</Td>
          {liveData.map((_, idx) => (<Td key={idx}></Td>))}
        </motion.tr>
        {enabledLines.wealth && (
          <motion.tr custom={1} initial="hidden" animate="visible" variants={rowVariants}>
            <Td sticky align="left">Wealth</Td>
            {liveData.map((proj, idx) => (<Td key={idx}>{formatCurrency(proj.wealthRevenue)}</Td>))}
          </motion.tr>
        )}
        {enabledLines.wealth && (
          <motion.tr custom={2} initial="hidden" animate="visible" variants={rowVariants}>
            <Td sticky align="left" className="text-succession-text-secondary pl-8">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_payout_pct' })}</Td>
            {liveData.map((_, idx) => (<Td key={idx}>{formatPercent(payout)}</Td>))}
          </motion.tr>
        )}
        {enabledLines.life && (
          <motion.tr custom={3} initial="hidden" animate="visible" variants={rowVariants}>
            <Td sticky align="left">Life</Td>
            {liveData.map((proj, idx) => (<Td key={idx}>{formatCurrency(proj.lifeRevenue)}</Td>))}
          </motion.tr>
        )}
        {enabledLines.pc && (
          <motion.tr custom={4} initial="hidden" animate="visible" variants={rowVariants}>
            <Td sticky align="left">P&C</Td>
            {liveData.map((proj, idx) => (<Td key={idx}>{formatCurrency(proj.pcRevenue)}</Td>))}
          </motion.tr>
        )}
        {enabledLines.group && (
          <motion.tr custom={5} initial="hidden" animate="visible" variants={rowVariants}>
            <Td sticky align="left">Group</Td>
            {liveData.map((proj, idx) => (<Td key={idx}>{formatCurrency(proj.groupRevenue)}</Td>))}
          </motion.tr>
        )}
        <motion.tr custom={6} initial="hidden" animate="visible" variants={rowVariants}>
          <Td sticky align="left" className="font-medium">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_net_revenue' })}</Td>
          {liveData.map((proj, idx) => (<Td key={idx} className="font-medium">{formatCurrency(proj.netRevenue)}</Td>))}
        </motion.tr>

        <motion.tr custom={7} initial="hidden" animate="visible" variants={rowVariants} style={{ background: expenseBg }}>
          <Td sticky align="left" className="font-semibold" style={{ background: expenseBg }}>{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_expense_projections' })}</Td>
          {liveData.map((_, idx) => (<Td key={idx}></Td>))}
        </motion.tr>
        {showDirectExpenses && (
          <motion.tr custom={8} initial="hidden" animate="visible" variants={rowVariants}>
            <Td sticky align="left">
              <span className="flex items-center gap-2">
                {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_direct_expenses' })}
                <InfoTooltip content="Costs directly tied to generating revenue, such as owner's compensation. These are deducted before calculating operating profit." />
              </span>
            </Td>
            {liveData.map((proj, idx) => (<Td key={idx}>{formatCurrency(proj.directExpenses)}</Td>))}
          </motion.tr>
        )}
        <motion.tr custom={9} initial="hidden" animate="visible" variants={rowVariants}>
          <Td sticky align="left">
            <span className="flex items-center gap-2">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_operating_expenses' })}
              <InfoTooltip content="The ongoing costs to run your business—rent, utilities, staff, marketing, and administrative expenses. These are deducted from net revenue to calculate EBIT." />
            </span>
          </Td>
          {liveData.map((proj, idx) => (<Td key={idx}>{formatCurrency(proj.operatingExpenses)}</Td>))}
        </motion.tr>

        <motion.tr custom={12} initial="hidden" animate="visible" variants={rowVariants} style={{ background: fcfBg }}>
          <Td sticky align="left" className="font-semibold" style={{ background: fcfBg }}>{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_fcf_calculations' })}</Td>
          {liveData.map((_, idx) => (<Td key={idx}></Td>))}
        </motion.tr>
        <motion.tr custom={13} initial="hidden" animate="visible" variants={rowVariants}>
          <Td sticky align="left">
            <span className="flex items-center gap-2">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_ebit' })}
              <InfoTooltip content="Earnings Before Interest and Taxes. This is your operating profit—what's left after subtracting direct and operating expenses from net revenue." />
            </span>
          </Td>
          {liveData.map((proj, idx) => (<Td key={idx} className="font-medium">{formatCurrency(proj.ebit)}</Td>))}
        </motion.tr>
        <motion.tr custom={14} initial="hidden" animate="visible" variants={rowVariants}>
          <Td sticky align="left">
            <span className="flex items-center gap-2">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_tax' })}{inferredTaxRate ? ` (${formatPercent(inferredTaxRate)})` : ''}
              <InfoTooltip content="Estimated income tax on your operating profit. This is calculated using your marginal tax rate and reduces EBIT to arrive at free cash flow." />
            </span>
          </Td>
          {liveData.map((proj, idx) => (<Td key={idx}>{formatCurrency(proj.tax)}</Td>))}
        </motion.tr>
        <motion.tr custom={15} initial="hidden" animate="visible" variants={rowVariants}>
          <Td sticky align="left">
            <span className="flex items-center gap-2">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_fcf' })}
              <InfoTooltip content="Free Cash Flow—the cash your business generates after paying all expenses and taxes. This is the money available to owners and is the basis for valuation." />
            </span>
          </Td>
          {liveData.map((proj, idx) => (<Td key={idx} className="font-medium">{formatCurrency(proj.fcf)}</Td>))}
        </motion.tr>

        <motion.tr custom={16} initial="hidden" animate="visible" variants={rowVariants} style={{ background: pvBg }}>
          <Td sticky align="left" className="font-semibold" style={{ background: pvBg }}>{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_pv_calculations' })}</Td>
          {liveData.map((_, idx) => (<Td key={idx}></Td>))}
        </motion.tr>
        {showDiscountFactor && (
          <motion.tr custom={17} initial="hidden" animate="visible" variants={rowVariants}>
            <Td sticky align="left">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_discount_factor' })}</Td>
            {liveData.map((proj, idx) => (<Td key={idx}>{formatDecimal(proj.discountFactor)}</Td>))}
          </motion.tr>
        )}
        <motion.tr custom={18} initial="hidden" animate="visible" variants={rowVariants}>
          <Td sticky align="left">
            <span className="flex items-center gap-2">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_discount_rate_row' })}
              <InfoTooltip content="The rate used to convert future cash flows to today's dollars. It reflects the risk of your business—higher risk means a higher discount rate and lower present value." />
            </span>
          </Td>
          {liveData.map((proj, idx) => {
            if (idx === 0) return (<Td key={idx}></Td>);
            if (showDiscountFactor) {
              const df = Number(proj.discountFactor || 0);
              const t = Math.max(idx, 1);
              const r = df > 0 ? Math.pow(1 / df, 1 / t) - 1 : 0;
              return (<Td key={idx}>{formatPercent(r)}</Td>);
            }
            return (<Td key={idx}>{formatPercent(proj.discountRate)}</Td>);
          })}
        </motion.tr>
        <motion.tr custom={19} initial="hidden" animate="visible" variants={rowVariants}>
          <Td sticky align="left">
            <span className="flex items-center gap-2">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_pv_row' })}
              <InfoTooltip content="Present Value—what each year's future cash flow is worth in today's dollars. Future money is worth less than money today, so we discount it back." />
            </span>
          </Td>
          {liveData.map((proj, idx) => ((idx === 0 || proj.year === 'Current Year')
            ? (<Td key={idx}></Td>)
            : (<Td key={idx} className="font-semibold">{formatCurrency(proj.presentValue)}</Td>)
          ))}
        </motion.tr>
      </tbody>
    </Table>
  );
}


